import {
    LOAD_INSTITUTION_STUDY_TEAM_MEMBERS,
    LOAD_INSTITUTION_STUDY_TYPES,
    LOAD_INSTITUTION_STUDY_ROLES,
    LOAD_STUDY_METADATA,
    LOAD_STUDY_STAGES,
    LOADING_STUDY_STATE,
    STUDY_STATE_LOADED,
    SAVING_STUDY_STATE,
    STUDY_STATE_SAVED,
    SET_STUDY_TEAM_POTENTIAL_MATCH,
    CLEAR_STUDY,
    LOAD_INSTITUTION_USABLE_STUDY_TYPES,
} from '../actions/createStudyV2';
import lodash from 'lodash';
import PredicateField from 'binder/util/PredicateField';

export function getStudyTypesByInstitution(state, action) {
    if (!action || action.type !== LOAD_INSTITUTION_STUDY_TYPES) {
        return state;
    }

    const studyTypes = action.payload;
    const newState = lodash.cloneDeep(state);

    lodash.set(newState, 'study.studyTypes', studyTypes);
    return newState;
}

export function getUsableStudyTypesByInstitution(state, action) {
    if (!action || action.type !== LOAD_INSTITUTION_USABLE_STUDY_TYPES) {
        return state;
    }

    const studyTypes = action.payload;
    const newState = lodash.cloneDeep(state);

    lodash.set(newState, 'study.usableStudyTypes', studyTypes);
    return newState;
}

export function getStudyStages(state, action) {
    if (!action || action.type !== LOAD_STUDY_STAGES) {
        return state;
    }

    return {
        ...state,
        study: {
            ...state.study,
            studyStages: action.payload
        }
    };
}

export function getStudyRolesByInstitution(state, action) {
    if (!action || action.type !== LOAD_INSTITUTION_STUDY_ROLES) {
        return state;
    }

    const studyRoles = action.payload;
    const newState = lodash.cloneDeep(state);

    lodash.set(newState, 'study.studyRoles', studyRoles);
    return newState;
}

export function getTeamMembersByInstitution(state, action) {
    if (!action || action.type !== LOAD_INSTITUTION_STUDY_TEAM_MEMBERS) {
        return state;
    }

    const teamMembers = action.payload;
    const newState = lodash.cloneDeep(state);

    lodash.set(newState, 'study.teamMembers', teamMembers);
    return newState;
}

export function setPotentialResearchersToStudyTeam(state, action) {
    if (!action || action.type !== SET_STUDY_TEAM_POTENTIAL_MATCH) {
        return state;
    }

    const newTeamMembers = action.payload;
    const newState = lodash.cloneDeep(state);

    lodash.set(newState, 'study.fields.studyTeam', newTeamMembers);
    return newState;
}


export function getStudyMetadata(state, action) {
    if (!action || action.type !== LOAD_STUDY_METADATA) {
        return state;
    }

    const {
        institutionId,
        binderTemplate,
        descriptors,
        studyEssentials
    } = action.payload;

    const studyDetails = getStudyDetails(binderTemplate, descriptors, institutionId);
    const staffDescriptor = getStaffDescriptor(studyEssentials);
    const staffDescriptorId = lodash.get(staffDescriptor, 'descriptorId', null);
    const descriptorsKeyedById = lodash.keyBy(descriptors, 'id');

    const studyDetailsWithoutStaff = studyDetails.filter(studyDetail => studyDetail.descriptorId !== staffDescriptorId);

    const newState = lodash.cloneDeep(state);

    lodash.set(newState, 'study.binderTemplate', binderTemplate);
    lodash.set(newState, 'study.descriptors', descriptorsKeyedById);
    lodash.set(newState, 'study.studyDetails', studyDetailsWithoutStaff);

    lodash.set(newState, 'study.studyDetails', [...studyDetailsWithoutStaff, staffDescriptor]);
    

    if (staffDescriptor) {
        lodash.set(newState, 'study.staffDescriptor', staffDescriptor);
    }

    return newState;
}

export function getStudyDetails(binderTemplate, descriptors, institutionId) {
    const binderTemplatePredicateGroups = Array.from(binderTemplate.predicateGroups.values());
    const descriptorsKeyedById = lodash.keyBy(descriptors, 'id');
    return translateBinderTemplateRequirementToStudyRequirment(
        binderTemplatePredicateGroups,
        descriptorsKeyedById,
        institutionId);
}

function getStaffDescriptor(studyEssentials) {
    const staffDescriptor = lodash.find(studyEssentials, studyEssential => {
        return studyEssential.type === 'staff';
    });

    return staffDescriptor;
}

export function clearStudy(state, action) {
    if (!action || action.type !== CLEAR_STUDY) {
        return state;
    }
    const newState = lodash.cloneDeep(state);
    lodash.unset(newState, 'study');

    return newState;
}

export function loadingStudyState(state, action) {
    if (!action || action.type !== LOADING_STUDY_STATE) {
        return state;
    }
    const newState = lodash.cloneDeep(state);
    lodash.set(newState, 'study.loading', true);

    return newState;
}


export function studyStateLoaded(state, action) {
    if (!action || action.type !== STUDY_STATE_LOADED) {
        return state;
    }
    const newState = lodash.cloneDeep(state);
    lodash.set(newState, 'study.loading', false);

    return newState;
}


export function savingStudyState(state, action) {
    if (!action || action.type !== SAVING_STUDY_STATE) {
        return state;
    }
    const newState = lodash.cloneDeep(state);
    lodash.set(newState, 'study.saving', true);

    return newState;
}


export function studyStateSaved(state, action) {
    if (!action || action.type !== STUDY_STATE_SAVED) {
        return state;
    }
    const newState = lodash.cloneDeep(state);
    lodash.set(newState, 'study.saving', false);

    return newState;
}


/**
 * This function takes in the binder requirements (binder template predicate groups) 
 * from the binder template and translates them into study requirements
 * @param {Array<Object>} binderRequirments An array of binder template predicate groups
 * @param {Object} descriptors Institution descriptors keyed by descriptor id.
 * @param {String} institutionId ID of the institution that owns the study.
 * @returns [StudyRequirement]
 */
export function translateBinderTemplateRequirementToStudyRequirment(binderRequirments, descriptors, institutionId) {
    return lodash.map(binderRequirments, binderRequirement => {
        const predicateField = new PredicateField(binderRequirement.key, binderRequirement.value, {});
        const binderRequirementDescriptorId = lodash.get(binderRequirement, 'key.descriptorId');
        const binderRequirementValueSetType = lodash.get(binderRequirement, 'key.value.type');
        const descriptor = descriptors[binderRequirementDescriptorId];
        const name = predicateField.getLabel(descriptor);
        const displayName = predicateField.getDisplayName(descriptor);
        const label = predicateField.getLabelV4(descriptor);
        const dataType = descriptor && descriptor.dataType && descriptor.dataType.toLowerCase();
        const inputFieldType = binderRequirementValueSetType === 'ScalarValue' ? `${binderRequirementValueSetType}_${dataType}` : `${binderRequirementValueSetType}`;
        const instructions = predicateField.getInstructionalText(descriptor);
        const shouldBeAllValuesSelected = predicateField.getDescriptorsThatShouldHaveAllValuesSelected(descriptor);

        return {
            name,
            displayName,
            label,
            studyId: null,
            institutionId,
            dataSources: null,
            value: lodash.get(binderRequirement, 'key.value.selectedValues'),
            dataSourceId: lodash.get(binderRequirement, 'key.value.dataSourceId'),
            descriptorId: lodash.get(binderRequirement, 'key.descriptorId'),
            instructions,
            inputFieldType: studyInputFieldMap[inputFieldType],
            dataType,
            shouldBeAllValuesSelected
        };
    });
}

/**
 * This is an early attempt at mapping the Complion Query Value Set types
 * to the study field input types so that the binder template requirements 
 * be collected in a somewhat consistent manner. [WIP]
 */
const studyInputFieldMap = {
    FixedValue: 'single-value',
    ValueList: 'single-value',
    ScalarValue_dateonly: 'date-value',
    ScalarValue_datetime: 'date-time-value',
    ScalarValue_small_text: 'single-value',
    ScalarValue_integer: 'single-value',
    SelectList: 'single-select',
    MultiselectList: 'multi-select',
    DataSourceSingleValue: 'single-select',
    DataSourceMultipleValue: 'multi-select',
    DataSourceValueList: 'multi-select',
    DatasetFixedValue: 'single-value',
    DatasetValueList: 'single-value',
    DatasetSingleValue: 'single-select',
    DatasetMultiselectList: 'multi-select',
};
